import React, { useState, useEffect, useCallback } from "react";
import { TrashIcon } from "@heroicons/react/24/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import SearchBar from "../../ui/SearchBar";
import { PageLoadingView } from "../../common/PageLoadingView";
import { PageErrorView } from "../../common/PageErrorView";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import { useSpecialEvents } from "../../../hooks/useSpecialEvents";
import { useUser } from "../../../context/UserInfoProvider";
import { DataManagementSidebar } from "./subcomponents/DataManagementSidebar";
import { SpecialEventItem } from "../../../api/specialEventsApi";
import { createPortal } from "react-dom";
import ImageUploader from "../../form/ImageUploader";

interface ValidationErrors {
  name?: string;
  description?: string;
  location?: string;
  imageFile?: string;
  link?: string;
  frequency?: string;
}

const ALL_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const WEEKEND_DAYS = ["Sat", "Sun"];

const dayLabels: { [key: string]: string } = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

// Tooltip component that shows on hover using Tailwind utility classes.
const InfoTooltip: React.FC<{ message: string }> = ({ message }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const show = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    setCoords({ x: rect.left + rect.width / 2, y: rect.top });
    setVisible(true);
  };
  const hide = () => setVisible(false);

  return (
    <>
      <InformationCircleIcon
        className="w-4 h-4 text-gray-400 cursor-help"
        onMouseEnter={show}
        onMouseLeave={hide}
      />
      {visible &&
        createPortal(
          <div
            className="fixed z-[9999] px-2 py-1 rounded bg-gray-900 bg-opacity-80 text-white text-xs whitespace-nowrap"
            style={{
              top: coords.y - 8,
              left: coords.x,
              transform: "translate(-50%, -100%)",
            }}
          >
            {message}
          </div>,
          document.body
        )}
    </>
  );
};

const SpecialEventsData: React.FC = () => {
  const { hotelId } = useUser();

  const {
    items,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    pagination,
    setPage,
    setLimit,
    refresh,
    setSearchTerm,
  } = useSpecialEvents({ hotelId: hotelId || "" });

  // Local edits state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  // Preset non-weekday frequencies
  const [presetFrequency, setPresetFrequency] = useState<
    "Biweekly" | "Monthly" | "Seasonal" | null
  >(null);
  // Fully custom, user-entered frequency
  const [useCustomFrequency, setUseCustomFrequency] = useState(false);
  const [customFrequencyText, setCustomFrequencyText] = useState("");
  // Store the last uploaded S3 key so we send the correct value to the backend (not the preview data URL)
  const [pendingImageKey, setPendingImageKey] = useState<string | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const totalPages = Math.max(
    1,
    Math.ceil(pagination.total / pagination.limit)
  );

  const handleItemSelect = (item: SpecialEventItem) => {
    // If we're creating a new item and user clicks an existing one, warn them
    if (isCreating) {
      setConfirmationDialog({
        isOpen: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes for a new special event. Do you want to discard these changes and edit the selected event?",
        confirmText: "Discard Changes",
        cancelText: "Cancel",
        onConfirm: () => {
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
          setSelectedItem(item);
          setValidationErrors({});
          setUpdateError(null);
          setIsCreating(false);
          setPendingImageKey(null);
        },
      });
      return;
    }

    setSelectedItem(item);
    setValidationErrors({});
    setUpdateError(null);
    setIsCreating(false);
    setPendingImageKey(null);
  };

  const handleAddItem = () => {
    setSelectedItem({
      eventId: "",
      hotelId: hotelId || "",
      name: "",
      description: "",
      location: "",
      imageFile: "",
      link: "",
      frequency: "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setValidationErrors({});
    setUpdateError(null);
    setIsCreating(true);
    setPendingImageKey(null);
    setPresetFrequency(null);
    setUseCustomFrequency(false);
    setCustomFrequencyText("");
  };

  // Store pending image - only update DB on save
  const handleLocalImageUpload = useCallback(
    (key: string, previewUrl?: string) => {
      // Store the S3 key for saving to the database
      setPendingImageKey(key);

      // Store the preview URL for immediate display
      setSelectedItem((prev) => {
        if (!prev) return null;
        return { ...prev, imageFile: previewUrl || key };
      });

      // Clear any previous error
      setUpdateError(null);
    },
    [selectedItem]
  );

  const handleSave = async () => {
    if (!selectedItem) return;

    const currentFrequencyString = useCustomFrequency
      ? customFrequencyText.trim()
      : presetFrequency ?? convertDaysToFrequencyString(selectedDays);
    const itemToSave = { ...selectedItem, frequency: currentFrequencyString };

    // Validate the form
    const errors = validateEvent(itemToSave);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      setIsUpdating(true);
      setUpdateError(null);
      setValidationErrors({});

      // Handle imageFile updates - only send if there's a pending upload or if it was deleted
      const updateData: any = {
        name: itemToSave.name,
        description: itemToSave.description,
        location: itemToSave.location,
        link: itemToSave.link,
        frequency: itemToSave.frequency,
      };

      if (isCreating) {
        // Step 1: Create new event without image
        const createdEvent = await createItem(
          updateData as unknown as Omit<
            SpecialEventItem,
            "eventId" | "createdAt" | "updatedAt"
          >
        );

        // Step 2: If there's an image to upload, update the event with it
        if (pendingImageKey) {
          try {
            await updateItem(createdEvent.eventId, {
              imageFile: pendingImageKey,
            });
          } catch (imageError) {
            console.error("Error updating image after creation:", imageError);
            setUpdateError(
              "Event created successfully, but failed to upload image. You can upload it later."
            );
          }
        }

        // Refresh to get the new event with signed URL
        await refresh();
      } else {
        // For updates, handle imageFile if there's a pending upload OR if it was deleted
        if (pendingImageKey) {
          updateData.imageFile = pendingImageKey;
        } else if (selectedItem.imageFile === "") {
          // Image was deleted, explicitly set to empty
          updateData.imageFile = "";
        }

        // Update the event - updateItem returns the updated event with signed URL in _media
        const updatedEvent = await updateItem(
          selectedItem.eventId,
          updateData as unknown as Partial<SpecialEventItem>
        );

        // Update selectedItem with the response that includes the signed URL
        setSelectedItem(updatedEvent);
      }

      // Close sidebar for create flow
      setSelectedItem(null);
      setIsCreating(false);
      setPendingImageKey(null);
    } catch (error) {
      console.error("Error saving special event:", error);
      setUpdateError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while saving the special event"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    setConfirmationDialog({
      isOpen: true,
      title: "Delete Special Event",
      message: "Are you sure you want to delete this special event?",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));

        try {
          setIsUpdating(true);
          await deleteItem(itemId);
          await refresh();
        } catch (error) {
          console.error("Error deleting special event:", error);
          setUpdateError(
            error instanceof Error
              ? error.message
              : "An unexpected error occurred while deleting the special event"
          );
        } finally {
          setIsUpdating(false);
        }
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    if ([10, 20, 50].includes(newLimit)) {
      setLimit(newLimit);
    }
  };

  const validateEvent = (
    event: Partial<SpecialEventItem>
  ): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!event.name?.trim()) {
      errors.name = "Name is required";
    }

    if (!event.description?.trim()) {
      errors.description = "Description is required";
    }

    if (!event.location?.trim()) {
      errors.location = "Location is required";
    }

    if (event.link && !isValidUrl(event.link)) {
      errors.link = "Please enter a valid URL";
    }

    return errors;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const convertFrequencyStringToDays = (frequency: string): string[] => {
    if (!frequency) return [];
    const lowerFrequency = frequency.toLowerCase();
    if (lowerFrequency === "daily") return [...ALL_DAYS];
    if (lowerFrequency === "weekdays") return [...WEEKDAYS];
    if (lowerFrequency === "weekend") return [...WEEKEND_DAYS];
    return frequency
      .split(",")
      .map((day) => day.trim())
      .filter((day) => ALL_DAYS.includes(day));
  };

  const convertDaysToFrequencyString = (days: string[]): string => {
    if (days.length === 0) return "";
    if (
      days.length === ALL_DAYS.length &&
      ALL_DAYS.every((day) => days.includes(day))
    )
      return "Daily";
    if (
      days.length === WEEKDAYS.length &&
      WEEKDAYS.every((day) => days.includes(day)) &&
      !days.some((day) => WEEKEND_DAYS.includes(day))
    )
      return "Weekdays";
    if (
      days.length === WEEKEND_DAYS.length &&
      WEEKEND_DAYS.every((day) => days.includes(day)) &&
      !days.some((day) => WEEKDAYS.includes(day))
    )
      return "Weekend";
    return days
      .sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b))
      .join(", ");
  };

  useEffect(() => {
    if (selectedItem) {
      const freq = selectedItem.frequency || "";
      const lower = freq.toLowerCase();
      if (lower.includes("biweek")) {
        setPresetFrequency("Biweekly");
        setUseCustomFrequency(false);
        setCustomFrequencyText("");
        setSelectedDays([]);
      } else if (lower.includes("month")) {
        setPresetFrequency("Monthly");
        setUseCustomFrequency(false);
        setCustomFrequencyText("");
        setSelectedDays([]);
      } else if (lower.includes("season")) {
        setPresetFrequency("Seasonal");
        setUseCustomFrequency(false);
        setCustomFrequencyText("");
        setSelectedDays([]);
      } else {
        const mappedDays = convertFrequencyStringToDays(freq);
        if (mappedDays.length > 0 || freq === "") {
          setPresetFrequency(null);
          setUseCustomFrequency(false);
          setCustomFrequencyText("");
          setSelectedDays(mappedDays);
        } else {
          setPresetFrequency(null);
          setUseCustomFrequency(true);
          setCustomFrequencyText(selectedItem.frequency || "");
          setSelectedDays([]);
        }
      }
    } else {
      setSelectedDays([]);
      setPresetFrequency(null);
      setUseCustomFrequency(false);
      setCustomFrequencyText("");
    }
  }, [selectedItem]);

  const filteredItems = items; // server-side filtered

  if (isLoading && !selectedItem) {
    return <PageLoadingView />;
  }

  return (
    <div className="w-full h-screen flex flex-col text-gray-800 dark:text-white overflow-hidden">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Special Events</h1>
        <button
          onClick={handleAddItem}
          className="bg-blue-600 text-white py-1 px-4 rounded hover:bg-blue-500"
        >
          + Add Event
        </button>
      </div>

      {/* Search Bar */}
      <div className="px-6">
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={(q) => {
            setSearchQuery(q);
            if (q === "") setSearchTerm("");
          }}
          onSearchSubmit={() => {
            setIsSearching(true);
            setSearchTerm(searchQuery);
            // Reset searching state after a short delay to allow the hook to take over
            setTimeout(() => setIsSearching(false), 100);
          }}
          isLoading={isSearching}
          placeholder="Search for an event"
        />
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto p-6">
        {error ? (
          <PageErrorView
            title="Error loading special events"
            onRetry={refresh}
          />
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchQuery ? "No events match your search" : "No events found"}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredItems.map((item) => (
              <div
                key={item.eventId}
                className="py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => handleItemSelect(item)}
              >
                {/* Image preview */}
                <div className="w-10 h-10 rounded overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-4">
                  {item.imageFile ? (
                    <img
                      src={item.imageFile}
                      alt={`${item.name} image`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <svg
                      className="w-6 h-6 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium">{item.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {item.location} | {item.frequency}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {item.description}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.eventId);
                  }}
                  className="text-red-500 hover:text-red-600"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Show
            </span>
            <select
              value={pagination.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 pr-8"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              items per page
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <DataManagementSidebar
        title={isCreating ? "Add Special Event" : "Edit Special Event"}
        selectedItem={selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setIsCreating(false);
          setValidationErrors({});
          setUpdateError(null);
          setPendingImageKey(null);
          setPresetFrequency(null);
          setUseCustomFrequency(false);
          setCustomFrequencyText("");
        }}
        onSave={handleSave}
        isUpdating={isUpdating}
        updateError={updateError}
      >
        <div>
          <label className="block text-sm font-medium mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={selectedItem?.name || ""}
            onChange={(e) =>
              setSelectedItem((prev) =>
                prev ? { ...prev, name: e.target.value } : null
              )
            }
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 ${
              validationErrors.name ? "border-red-500" : ""
            }`}
          />
          {validationErrors.name && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={selectedItem?.description || ""}
            onChange={(e) =>
              setSelectedItem((prev) =>
                prev ? { ...prev, description: e.target.value } : null
              )
            }
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 ${
              validationErrors.description ? "border-red-500" : ""
            }`}
          />
          {validationErrors.description && (
            <p className="mt-1 text-sm text-red-500">
              {validationErrors.description}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Location <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={selectedItem?.location || ""}
            onChange={(e) =>
              setSelectedItem((prev) =>
                prev ? { ...prev, location: e.target.value } : null
              )
            }
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 ${
              validationErrors.location ? "border-red-500" : ""
            }`}
          />
          {validationErrors.location && (
            <p className="mt-1 text-sm text-red-500">
              {validationErrors.location}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Event Image</label>
          {isCreating ? (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please save the event first to upload an image
              </p>
            </div>
          ) : !selectedItem?.eventId ? (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Event ID is missing. Please refresh the page or select a
                different event.
              </p>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              <ImageUploader
                entityType="hotelevent-image"
                entityId={selectedItem.eventId}
                currentImageUrl={selectedItem?.imageFile}
                onUploadComplete={handleLocalImageUpload}
                onUploadError={(error) => {
                  setValidationErrors((prev) => ({
                    ...prev,
                    imageFile: error,
                  }));
                }}
                onDeleteImage={() => {
                  setSelectedItem((prev) =>
                    prev ? { ...prev, imageFile: "" } : null
                  );
                  setPendingImageKey(null); // Clear pending upload if image is deleted
                  setValidationErrors((prev) => ({
                    ...prev,
                    imageFile: undefined,
                  }));
                }}
                disabled={isUpdating}
              />
            </div>
          )}
          {validationErrors.imageFile && (
            <p className="mt-1 text-sm text-red-500">
              {validationErrors.imageFile}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-1">
            Link
            <InfoTooltip message="Enter a full URL starting with http:// or https://" />
          </label>
          <input
            type="text"
            value={selectedItem?.link || ""}
            onChange={(e) =>
              setSelectedItem((prev) =>
                prev ? { ...prev, link: e.target.value } : null
              )
            }
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 ${
              validationErrors.link ? "border-red-500" : ""
            }`}
          />
          {validationErrors.link && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.link}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Frequency</label>
          {/* Presets for non-weekday-based frequencies */}
          <div className="flex flex-wrap gap-2 mt-1 mb-2">
            <button
              type="button"
              onClick={() => {
                setUseCustomFrequency(false);
                setCustomFrequencyText("");
                setPresetFrequency("Seasonal");
                setSelectedDays([]);
              }}
              className={`px-3 py-1 border rounded-md text-sm ${
                presetFrequency === "Seasonal"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              Seasonal
            </button>
            <button
              type="button"
              onClick={() => {
                setUseCustomFrequency(false);
                setCustomFrequencyText("");
                setPresetFrequency("Biweekly");
                setSelectedDays([]);
              }}
              className={`px-3 py-1 border rounded-md text-sm ${
                presetFrequency === "Biweekly"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              Biweekly
            </button>
            <button
              type="button"
              onClick={() => {
                setUseCustomFrequency(false);
                setCustomFrequencyText("");
                setPresetFrequency("Monthly");
                setSelectedDays([]);
              }}
              className={`px-3 py-1 border rounded-md text-sm ${
                presetFrequency === "Monthly"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              Monthly
            </button>
          </div>
          {/* Weekday selection; selecting a day after choosing a preset reverts to days-of-week mode */}
          <div className="flex flex-wrap gap-2 mt-1 mb-2">
            {ALL_DAYS.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => {
                  setPresetFrequency(null);
                  setUseCustomFrequency(false);
                  setCustomFrequencyText("");
                  setSelectedDays((prevDays) =>
                    prevDays.includes(day)
                      ? prevDays.filter((d) => d !== day)
                      : [...prevDays, day]
                  );
                }}
                className={`px-3 py-1 border rounded-md text-sm 
                  ${
                    selectedDays.includes(day)
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
              >
                {dayLabels[day]}
              </button>
            ))}
          </div>
          {/* Custom frequency toggle and display/input */}
          <div className="flex items-center gap-2 mt-2 mb-1">
            <input
              id="useCustomFrequency"
              type="checkbox"
              checked={useCustomFrequency}
              onChange={(e) => {
                const checked = e.target.checked;
                setUseCustomFrequency(checked);
                if (checked) {
                  setPresetFrequency(null);
                  setSelectedDays([]);
                } else {
                  setCustomFrequencyText("");
                }
              }}
            />
            <label htmlFor="useCustomFrequency" className="text-sm">
              Use custom frequency
            </label>
          </div>
          <input
            type="text"
            readOnly={!useCustomFrequency}
            value={
              useCustomFrequency
                ? customFrequencyText
                : presetFrequency ?? convertDaysToFrequencyString(selectedDays)
            }
            onChange={(e) => setCustomFrequencyText(e.target.value)}
            className={`w-full px-3 py-2 border rounded-md text-xs mt-1 ${
              useCustomFrequency
                ? "dark:bg-gray-800 dark:border-gray-700"
                : "dark:bg-gray-700 dark:border-gray-600 text-gray-500 dark:text-gray-400"
            }`}
            placeholder={
              useCustomFrequency
                ? "Enter a custom frequency (e.g., Every first Friday)"
                : "Frequency string will appear here"
            }
          />
        </div>
      </DataManagementSidebar>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={() =>
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))
        }
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        cancelText={confirmationDialog.cancelText}
      />
    </div>
  );
};

export default SpecialEventsData;
